import { useEffect, useState } from 'react'
import { Navigate, Outlet } from 'react-router'
import { billingApiService } from '../services/billingApiService'
import { useAuth } from '../contexts/AuthContext'
import { colors } from '../styles/theme'

/**
 * Signup card gate. A team whose plan requires a card and has none (a new Standard trial that has
 * verified its email but not yet added a card) is routed to the add-card step before the app opens.
 * Exempt/internal teams and teams with a card pass straight through, and the gate fails open on a
 * transient status error so a real customer is never locked out by a hiccup.
 */
export default function CardGate() {
  const { user } = useAuth()
  // A closed team (V085) is not asked for a card: it does no work, and its billing page stays reachable.
  const closed = !!user?.teamClosedAt
  const [state, setState] = useState<'checking' | 'blocked' | 'ok'>(closed ? 'ok' : 'checking')

  useEffect(() => {
    if (closed) return
    let cancelled = false
    billingApiService.getStatus()
      .then(s => {
        if (cancelled) return
        setState(s.billingAvailable && s.needsPaymentMethod ? 'blocked' : 'ok')
      })
      .catch(() => { if (!cancelled) setState('ok') }) // fail open
    return () => { cancelled = true }
  }, [closed])

  if (state === 'checking') {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100vh', background: colors.background, color: colors.textSecondary, fontSize: '14px',
      }}>
        Loading…
      </div>
    )
  }

  if (state === 'blocked') {
    return <Navigate to="/app/onboarding/add-card" replace />
  }

  return <Outlet />
}
